import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Divider,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  useTheme,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import toast from 'react-hot-toast';
import {
  Close,
  Photo,
  AttachFile,
  LocationOn,
  Add,
  Delete,
  CloudUpload,
  Email,
  Phone,
  Poll as PollIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/types';
import { fetchFeedTypes, createFeed, clearFeedError } from '@/store';
import { CreateFeedRequest, MediaItem } from '@/constants/types';
import { USER_ROLES } from '@/constants/app';
import { useAuth } from '@/hooks';
import { uploadService } from '@/services/upload/uploadService';
import { JobPostForm } from './JobPostForm';
import { createJobPostValidationSchema } from '@/validation/jobValidation';
import { SlateRichTextEditor } from './SlateRichTextEditor';
import { Descendant, Text as SlateText, Element as SlateElement } from 'slate';
import { CanvasPdfViewer } from './feed/CanvasPdfViewer';

// Helper functions to check document types
const isDocumentType = (item: MediaItem) => {
  const mimeType = item.mimeType || '';
  return mimeType === 'application/pdf' ||
         mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
         mimeType === 'application/msword' ||
         mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
         mimeType === 'application/vnd.ms-powerpoint';
};

const isPdfType = (item: MediaItem) => {
  const mimeType = item.mimeType || '';
  return mimeType === 'application/pdf';
};

// Helper function to convert Slate.js to HTML
const serializeSlateToHtml = (nodes: Descendant[]): string => {
  return nodes.map((node) => serializeNode(node)).join('');
};

const serializeNode = (node: Descendant): string => {
  if (SlateText.isText(node)) {
    let text = node.text;
    if (node.code) text = `<code>${text}</code>`;
    if (node.bold) text = `<strong>${text}</strong>`;
    if (node.italic) text = `<em>${text}</em>`;
    if (node.underline) text = `<u>${text}</u>`;
    if (node.strikethrough) text = `<s>${text}</s>`;
    return text;
  }

  if (SlateElement.isElement(node)) {
    const children = node.children.map((n) => serializeNode(n)).join('');
    const align = (node as any).align;
    const style = align ? ` style="text-align: ${align}"` : '';

    switch (node.type) {
      case 'block-quote':
        return `<blockquote${style}>${children}</blockquote>`;
      case 'heading-one':
        return `<h1${style}>${children}</h1>`;
      case 'heading-two':
        return `<h2${style}>${children}</h2>`;
      case 'heading-three':
        return `<h3${style}>${children}</h3>`;
      case 'heading-four':
        return `<h4${style}>${children}</h4>`;
      case 'heading-five':
        return `<h5${style}>${children}</h5>`;
      case 'heading-six':
        return `<h6${style}>${children}</h6>`;
      case 'bulleted-list':
        return `<ul>${children}</ul>`;
      case 'numbered-list':
        return `<ol>${children}</ol>`;
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link':
        return `<a href="${node.url}" target="_blank" rel="noopener noreferrer">${children}</a>`;
      case 'paragraph':
      default:
        return `<p${style}>${children}</p>`;
    }
  }

  return '';
};

// Helper function to get plain text from Slate nodes
const getPlainTextFromSlate = (nodes: Descendant[]): string => {
  return nodes
    .map((node) => {
      if (SlateText.isText(node)) {
        return node.text;
      }
      if (SlateElement.isElement(node) && node.children) {
        return getPlainTextFromSlate(node.children);
      }
      return '';
    })
    .join('');
};

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
}

// Regular post validation schema
const regularPostValidationSchema = yup.object({
  feedType: yup
    .string()
    .required('Please select a Talky type'),
  title: yup
    .string()
    .max(200, 'Title must be less than 200 characters')
    .required('Title is required'),

  content: yup
    .string()
    .required('Description is required')
    .test('min-content', 'Description must be at least 3 characters', function(value) {
      if (!value) return false;
      try {
        const slateNodes = JSON.parse(value) as Descendant[];
        const plainText = getPlainTextFromSlate(slateNodes).trim();
        return plainText.length >= 3;
      } catch {
        return value.trim().length >= 3;
      }
    })
    .test('max-content', 'Description must be less than 5000 characters', function(value) {
      if (!value) return true;
      try {
        const slateNodes = JSON.parse(value) as Descendant[];
        const plainText = getPlainTextFromSlate(slateNodes);
        return plainText.length <= 5000;
      } catch {
        return value.length <= 5000;
      }
    }),
  location: yup
    .string()
    .max(100, 'Location must be less than 100 characters'),
  visibility: yup
    .string()
    .oneOf(['public', 'private', 'connections'], 'Invalid visibility option')
    .required('Visibility is required'),
  tags: yup
    .array()
    .of(yup.string())
    .max(10, 'Maximum 10 tags allowed'),
  poll: yup.object({
    question: yup.string().optional(),
    options: yup.array()
      .of(yup.string().test(
        'required-if-poll',
        'Option is required',
        function(value) {
          const { question } = this.options.context || (this.from && this.from[1].value) || {};
          if (question && question.trim().length > 0) {
            return value !== undefined && value.trim().length > 0;
          }
          return true;
        }
      ))
      .when('question', {
        is: (val: string) => val && val.trim().length > 0,
        then: (schema) => schema.min(2, 'At least 2 options are required for a poll').test(
          'min-options',
          'At least 2 non-empty options are required',
          (options) => options ? options.filter(opt => opt && opt.trim().length > 0).length >= 2 : false
        ),
        otherwise: (schema) => schema.optional()
      })
  }).optional(),
});

export const CreatePostModal: React.FC<CreatePostModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const theme = useTheme();
  
  const { feedTypes, isLoading, isCreating, error } = useSelector((state: RootState) => state.feed);
console.log(isCreating)
  const [uploadedMedia, setUploadedMedia] = useState<MediaItem[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [tagInput, setTagInput] = useState('');
  const [isJobPost, setIsJobPost] = useState(false);
  const [isNormalPost, setIsNormalPost] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showPollOption, setShowPollOption] = useState(false);

  // Dynamic validation schema based on post type
  const getValidationSchema = () => {
    return isJobPost ? createJobPostValidationSchema : regularPostValidationSchema;
  };

  // Formik form setup following rules
  const formik = useFormik({
    initialValues: {
      feedType: '',
      title: '',
      content: '',
      location: '',
      visibility: 'public' as 'public' | 'private' | 'connections',
      tags: [] as string[],
      isAdminNotify: false,
      poll: {
        question: '',
        options: ['', '']
      },
      // Job-specific fields
      feedSpecificData: {
        jobTitle: '',
        jobDescription: '',
        jobType: '',
        experienceLevel: '',
        noOfVacancy: 1,
        noticeType: '',
        jobCategory: '',
        companyInfo: {
          companyname: user?.currentCompanyInfo?.companyname || '',
          addressLine1: user?.currentCompanyInfo?.addressLine1 || '',
          addressLine2: user?.currentCompanyInfo?.addressLine2 || '',
          city: user?.currentCompanyInfo?.city || '',
          state: user?.currentCompanyInfo?.state || '',
          pincode: user?.currentCompanyInfo?.pincode || '',
        },
        salary: {
          min: 0,
          max: 0,
          currency: 'INR',
          denomination: 'lakh',
          period: 'yearly',
        },
        skills: [] as string[],
        location: [] as string[],
        workMode: 'onsite',
        applicationDeadline: '',
        contactDetails: {
          email: '',
          mobile: '',
        },
      },
    },
    validationSchema: getValidationSchema(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      // If not in preview mode, show preview first
      if (!isPreviewMode) {
        handleShowPreview(values);
        return;
      }

      // If in preview mode, actually submit
      try {
        // Upload files during post creation
        const uploadedMediaItems = [];
        if (uploadedMedia.length > 0) {
          setUploadingFiles(true);
          setUploadProgress(0);

          for (let i = 0; i < uploadedMedia.length; i++) {
            const mediaItem = uploadedMedia[i];
            try {
              if (mediaItem.file) {
                const folder = mediaItem.type === 'image' ? 'feeds/images' : 'feeds/documents';
                const response = await uploadService.uploadSingle(mediaItem.file, { folder });

                uploadedMediaItems.push({
                  id: response.file.id,
                  type: mediaItem.type,
                  url: response.file.url,
                  originalName: response.file.fileName,
                  mimeType: mediaItem.mimeType,
                  size: mediaItem.size,
                });
              } else {
                uploadedMediaItems.push(mediaItem);
              }

              // Update progress
              setUploadProgress(((i + 1) / uploadedMedia.length) * 100);
            } catch (uploadErr: any) {
              console.error('Media upload failed:', uploadErr);
              toast.error(`Failed to upload ${mediaItem.originalName}: ${uploadErr.message || 'Unknown error'}`);
              throw uploadErr; // Rethrow to stop the post creation
            }
          }

          setUploadingFiles(false);
        }

        let feedData: CreateFeedRequest;

        if (isJobPost) {
          // For job posts, generate title and content from job data
          const jobData = values.feedSpecificData;

          // Convert job description from Slate.js to HTML
          let htmlJobDescription = '';
          try {
            if (jobData.jobDescription) {
              const slateNodes = JSON.parse(jobData.jobDescription) as Descendant[];
              htmlJobDescription = serializeSlateToHtml(slateNodes);
            }
          } catch {
            // Fallback to plain text if parsing fails
            htmlJobDescription = jobData.jobDescription || '';
          }

          feedData = {
            title: jobData.jobTitle,
            content: htmlJobDescription,
            feedType: values.feedType,
            visibility: values.visibility,
            tags: values.tags,
            location: values.location,
            media: uploadedMediaItems,
            isAdminNotify: values.isAdminNotify,
            feedSpecificData: {
              ...jobData,
              jobDescription: htmlJobDescription,
            },
          };
        } else {
          // For regular posts, convert Slate.js content to HTML
          let htmlContent = '';
          try {
            if (values.content) {
              const slateNodes = JSON.parse(values.content) as Descendant[];
              htmlContent = serializeSlateToHtml(slateNodes);
            }
          } catch {
            // Fallback to plain text if parsing fails
            htmlContent = values.content;
          }

          const { feedSpecificData, ...regularFormData } = values;
          
          let pollData = undefined;
          if (isNormalPost && values.poll.question.trim()) {
            const validOptions = values.poll.options
              .filter((opt: string) => opt && opt.trim().length > 0)
              .map((opt: string) => ({ text: opt.trim() }));
            
            if (validOptions.length >= 2) {
              pollData = {
                question: values.poll.question.trim(),
                options: validOptions
              };
            }
          }

          feedData = {
            ...regularFormData,
            content: htmlContent,
            media: uploadedMediaItems,
            poll: pollData,
            isAdminNotify: values.isAdminNotify
          };
        }

        await dispatch(createFeed(feedData)).unwrap();
        console.log('calling')
        toast.success('Post created successfully!');
        handleClose();
      } catch (err: any) {
        console.error('Failed to create feed:', err);

        // Handle validation errors
        if (err.errors && Array.isArray(err.errors)) {
          err.errors.forEach((validationError: { field: string; message: string }) => {
            toast.error(validationError.message);
          });
          setSubmitError(err.message || 'Validation failed');
        } else {
          // Handle general errors
          const errorMessage = err.message || 'Failed to create post';
          setSubmitError(errorMessage);
          toast.error(errorMessage);
        }
      }
    },
  });

  // Handle preview mode
  const handleShowPreview = (values: any) => {
    let htmlContent = '';

    if (isJobPost) {
      // For job posts, convert job description to HTML
      try {
        if (values.feedSpecificData.jobDescription) {
          const slateNodes = JSON.parse(values.feedSpecificData.jobDescription) as Descendant[];
          htmlContent = serializeSlateToHtml(slateNodes);
        }
      } catch {
        htmlContent = values.feedSpecificData.jobDescription || '';
      }
    } else {
      // For regular posts, convert content to HTML
      try {
        if (values.content) {
          const slateNodes = JSON.parse(values.content) as Descendant[];
          htmlContent = serializeSlateToHtml(slateNodes);
        }
      } catch {
        htmlContent = values.content;
      }
    }

    setPreviewData({
      ...values,
      htmlContent,
      feedTypeName: feedTypes.find(type => type._id === values.feedType)?.name,
      poll: isNormalPost && values.poll.question.trim() ? {
        question: values.poll.question.trim(),
        options: values.poll.options.filter((opt: string) => opt && opt.trim().length > 0).map((opt: string) => ({ text: opt.trim(), votes: 0 }))
      } : undefined
    });
    setIsPreviewMode(true);
  };

  const handleBackToEdit = () => {
    setIsPreviewMode(false);
    setPreviewData(null);
  };

  const handleConfirmPublish = () => {
    // Trigger the actual submit
    formik.handleSubmit();
  };

  // Memoize callbacks to prevent unnecessary re-renders
  // Formik's setFieldValue and setFieldTouched are stable functions
  const handleContentChange = useCallback((value: string) => {
    formik.setFieldValue('content', value);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContentBlur = useCallback(() => {
    formik.setFieldTouched('content', true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      // Reset states when modal opens
      dispatch(clearFeedError());
      setSubmitError(null);

      // Fetch feed types if needed
      // if (feedTypes.length === 0) {
        dispatch(fetchFeedTypes());
      // }
    }
  }, [open, feedTypes?.length, dispatch]);

  // Monitor feedType changes to switch between job and regular post modes
  useEffect(() => {
    const selectedFeedType = feedTypes.find(type => type._id === formik.values.feedType);
    const isJob = selectedFeedType?.name.toLowerCase() === 'job';
    const isPost = selectedFeedType?.name.toLowerCase() === 'post';
    setIsJobPost(isJob);
    setIsNormalPost(isPost);
  }, [formik.values.feedType, feedTypes]);

  // Clear error when user starts editing
  useEffect(() => {
    if (submitError && (formik.values.feedType || formik.values.title || formik.values.content)) {
      setSubmitError(null);
    }
  }, [formik.values.feedType, formik.values.title, formik.values.content, submitError]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, fileType: 'image' | 'document') => {
    try {
      const files = Array.from(event.target.files || []);
      if (files.length === 0) return;

      // Validate file sizes
      const maxSize = 10 * 1024 * 1024; // 10MB
      const oversizedFiles = files.filter(file => file.size > maxSize);
      if (oversizedFiles.length > 0) {
        toast.error(`Some files exceed the 10MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
        return;
      }

      const newMediaItems = files.map((file) => {
        const mediaItem: MediaItem = {
          id: `temp-${Date.now()}-${Math.random()}`,
          type: fileType,
          url: URL.createObjectURL(file),
          originalName: file.name,
          mimeType: file.type,
          size: file.size,
          file: file, // Store the actual file for upload during post creation
        };

        return mediaItem;
      });

      setUploadedMedia(prev => [...prev, ...newMediaItems]);
      toast.success(`${files.length} file(s) added successfully`);
      event.target.value = '';
    } catch (err: any) {
      console.error('File upload error:', err);
      toast.error(err.message || 'Failed to upload files');
    }
  };

  const handleRemoveMedia = (index: number) => {
    setUploadedMedia(prev => {
      const mediaToRemove = prev[index];
      if (mediaToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(mediaToRemove.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formik.values.tags.includes(tagInput.trim()) && formik.values.tags.length < 10) {
      formik.setFieldValue('tags', [...formik.values.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    formik.setFieldValue('tags', formik.values.tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddPollOption = () => {
    const currentOptions = [...formik.values.poll.options];
    if (currentOptions.length < 5) {
      formik.setFieldValue('poll.options', [...currentOptions, '']);
    } else {
      toast.error('Maximum 5 options allowed');
    }
  };

  const handleRemovePollOption = (index: number) => {
    const currentOptions = [...formik.values.poll.options];
    if (currentOptions.length > 2) {
      const newOptions = currentOptions.filter((_, i) => i !== index);
      formik.setFieldValue('poll.options', newOptions);
    } else {
      toast.error('Minimum 2 options required');
    }
  };

  const handleClose = () => {
    // Clean up object URLs
    console.log('closing')
    uploadedMedia.forEach(media => {
      if (media.url.startsWith('blob:')) {
        URL.revokeObjectURL(media.url);
      }
    });

    formik.resetForm();
    setUploadedMedia([]);
    setUploadingFiles(false);
    setUploadProgress(0);
    setTagInput('');
    setIsJobPost(false);
    setIsPreviewMode(false);
    setPreviewData(null);
    setSubmitError(null);
    setShowPollOption(false);

    // Reset Redux states
    dispatch(clearFeedError());

    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableAutoFocus
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'visible',
          }
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          overflow: 'visible',
        },
      }}
    >
      {/* <form onSubmit={formik.handleSubmit}> */}
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {isJobPost ? 'Create Job Posting' : 'Create New Talky'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isJobPost ? 'Post a job opportunity for candidates' : 'Share your thoughts with the community'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            py: 2,
            '& .rdw-editor-toolbar': {
              position: 'relative',
              zIndex: 1400, // Higher than Dialog backdrop (1300)
            }
          }}
        >
          {submitError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSubmitError(null)}
            >
              {submitError}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isPreviewMode && previewData ? (
            // Preview Mode
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                Preview Your Post
              </Typography>

              {/* Preview Card */}
              <Card
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  mb: 3
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  {/* User Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      {user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {user?.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          Just now
                        </Typography>
                        {previewData.location && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {previewData.location}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={previewData.feedTypeName === 'job' ? 'Free Job Post' : previewData.feedTypeName?.charAt(0).toUpperCase() + previewData.feedTypeName?.slice(1)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  {/* Post Title */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {isJobPost ? previewData.feedSpecificData?.jobTitle : previewData.title}
                  </Typography>

                  {/* Post Content */}
                  <Box
                    sx={{
                      mb: 2,
                      '& p': { mb: 1 },
                      '& h1, & h2, & h3, & h4, & h5, & h6': { fontWeight: 600, mb: 1.5, mt: 1.5 },
                      '& ul, & ol': { pl: 3, mb: 1 },
                      '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        pl: 2,
                        py: 0.5,
                        fontStyle: 'italic',
                        color: 'text.secondary'
                      },
                      '& code': {
                        bgcolor: 'grey.100',
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontFamily: 'monospace'
                      },
                      '& a': {
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: previewData.htmlContent }}
                  />

                  {/* Tags */}
                  {previewData.tags && previewData.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {previewData.tags.map((tag: string, index: number) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}

                  {/* Media Preview */}
                  {uploadedMedia.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {uploadedMedia.map((media, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          {media.type === 'image' ? (
                            // Image Preview
                            <Box
                              component="img"
                              src={media.url}
                              alt={media.originalName}
                              sx={{
                                width: '100%',
                                maxHeight: 400,
                                objectFit: 'contain',
                                borderRadius: 2,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            // Document Preview
                            <Box>
                              {/* Document Info Header */}
                              <Box
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 2,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                                  p: 2,
                                  mb: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AttachFile sx={{ color: 'text.secondary', fontSize: 24 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                                      {media.originalName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {media.size > 1024 * 1024
                                        ? `${(media.size / (1024 * 1024)).toFixed(1)} MB`
                                        : `${(media.size / 1024).toFixed(1)} KB`}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Document Viewer */}
                              {isDocumentType(media) && isPdfType(media) && (
                                <Box
                                  sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    bgcolor: 'background.paper',
                                    p: 1,
                                  }}
                                >
                                  {isPdfType(media) ? (
                                    // Use custom PdfViewer for PDF files
                                    <CanvasPdfViewer url={media.url} fileName={media.originalName} />
                                  ) : (
                                    <></>
                                    // Use Google Docs viewer iframe for Word and PowerPoint files
                                    // <Box
                                    //   component="iframe"
                                    //   src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                                    //     media.url
                                    //   )}&embedded=true`}
                                    //   sx={{
                                    //     width: '100%',
                                    //     height: 500,
                                    //     border: 'none',
                                    //     borderRadius: 1,
                                    //   }}
                                    //   title={media.originalName || 'document'}
                                    //   onError={(e) => {
                                    //     console.error('Error loading document preview:', e);
                                    //   }}
                                    // />
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Poll Preview */}
                  {isNormalPost && previewData.poll && (
                    <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PollIcon color="primary" fontSize="small" />
                        {previewData.poll.question}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {previewData.poll.options.map((option: any, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <Typography variant="body2" sx={{ zIndex: 1 }}>
                              {option.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ zIndex: 1 }}>
                              0%
                            </Typography>
                            {/* Visual indicator for 0% */}
                            <Box 
                              sx={{ 
                                position: 'absolute', 
                                left: 0, 
                                top: 0, 
                                bottom: 0, 
                                width: '0%', 
                                bgcolor: 'primary.light', 
                                opacity: 0.1 
                              }} 
                            />
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Poll preview • 0 votes
                      </Typography>
                    </Box>
                  )}

                  {/* Job-specific preview fields */}
                  {isJobPost && previewData.feedSpecificData && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Job Details
                      </Typography>

                      {/* Key Information */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Job Type</Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {previewData.feedSpecificData.jobType || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Experience Level</Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {previewData.feedSpecificData.experienceLevel || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Work Mode</Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {previewData.feedSpecificData.workMode || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Vacancies</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {previewData.feedSpecificData.noOfVacancy || '1'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Notice Period</Typography>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                            {previewData.feedSpecificData.noticeType || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">Application Deadline</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {previewData.feedSpecificData.applicationDeadline || 'Not specified'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {/* Salary Information */}
                      {previewData.feedSpecificData.salary && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Salary Range
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {previewData.feedSpecificData.salary.min} - {previewData.feedSpecificData.salary.max}{' '}
                            {previewData.feedSpecificData.salary.denomination} {previewData.feedSpecificData.salary.currency}{' '}
                            ({previewData.feedSpecificData.salary.period})
                          </Typography>
                        </Box>
                      )}

                      {/* Skills */}
                      {previewData.feedSpecificData.skills && previewData.feedSpecificData.skills.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Required Skills
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {previewData.feedSpecificData.skills.map((skill: string, index: number) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Job Locations */}
                      {previewData.feedSpecificData.location && previewData.feedSpecificData.location.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Job Locations
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {previewData.feedSpecificData.location.map((loc: string, index: number) => (
                              <Chip
                                key={index}
                                label={loc}
                                size="small"
                                color="info"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Company Information */}
                      {previewData.feedSpecificData.companyInfo && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Company
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {previewData.feedSpecificData.companyInfo.companyname}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {previewData.feedSpecificData.companyInfo.addressLine1}
                            {previewData.feedSpecificData.companyInfo.addressLine2 && `, ${previewData.feedSpecificData.companyInfo.addressLine2}`}
                            <br />
                            {previewData.feedSpecificData.companyInfo.city}, {previewData.feedSpecificData.companyInfo.state} - {previewData.feedSpecificData.companyInfo.pincode}
                          </Typography>
                        </Box>
                      )}

                      {/* Contact Details */}
                      {previewData.feedSpecificData.contactDetails && (
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mt: 2 }}>
                          {previewData.feedSpecificData.contactDetails.email && (
                            <Button
                              variant="contained"
                              startIcon={<Email />}
                              href={`mailto:${previewData.feedSpecificData.contactDetails.email}`}
                              sx={{ textTransform: 'none' }}
                            >
                              Enquire via Email
                            </Button>
                          )}
                          {previewData.feedSpecificData.contactDetails.mobile && (
                            <Button
                              variant="outlined"
                              startIcon={<Phone />}
                              href={`tel:${previewData.feedSpecificData.contactDetails.mobile}`}
                              sx={{ textTransform: 'none' }}
                            >
                              Call to Enquire
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                This is how your post will appear to others. Review carefully before publishing.
              </Typography>
            </Box>
          ) : (
            // Edit Mode
            <Grid container spacing={3} mt={3}>
            {/* Feed Type and Visibility Row - Following new Grid pattern */}
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                select
                name="feedType"
                label="Talky Type"
                required
                value={formik.values.feedType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.feedType && Boolean(formik.errors.feedType)}
                helperText={formik.touched.feedType && formik.errors.feedType}
                variant="outlined"
                disabled={isLoading || feedTypes?.length === 0}
              >
                {isLoading ? (
                  <MenuItem disabled>
                    <em>Loading feed types...</em>
                  </MenuItem>
                ) : error ? (
                  <MenuItem disabled>
                    <em>Error loading feed types</em>
                  </MenuItem>
                ) : feedTypes?.length === 0 ? (
                  <MenuItem disabled>
                    <em>No feed types available</em>
                  </MenuItem>
                ) : (
                  feedTypes?.filter(type => type.name !== 'jobSeeker').map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.name === 'job' ? 'Free Job Post' : type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                      {/* <Chip 
                        label={type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      /> */}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            {/* <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                name="visibility"
                label="Visibility"
                required
                value={formik.values.visibility}
                onChange={formik.handleChange}
                variant="outlined"
              >
                <MenuItem value="public">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Public fontSize="small" color="success" />
                    <Typography>Public</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="connections">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Group fontSize="small" color="warning" />
                    <Typography>Connections</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="private">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lock fontSize="small" color="error" />
                    <Typography>Private</Typography>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid> */}

            {/* WhatsApp Notify Checkbox - Only for super_Admin */}
            {user?.role === USER_ROLES.SUPER_ADMIN && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isAdminNotify}
                      onChange={(e) => formik.setFieldValue('isAdminNotify', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Send WhatsApp notification all users
                    </Typography>
                  }
                />
              </Grid>
            )}

            {/* Conditionally render form fields based on post type */}
            {isJobPost ? (
              <Grid size={{ xs: 12 }}>
                <JobPostForm 
                  formik={{...formik, setFieldValue: formik.setFieldValue}} 
                  feedType={feedTypes.find(type => type._id === formik.values.feedType)?.name}
                />
              </Grid>
            ) : (
              <>
                {/* Title Field */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Title"
                    required
                    placeholder="Give your post a catchy title..."
                    value={formik.values.title}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (showPollOption) {
                        formik.setFieldValue('poll.question', e.target.value);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.title && Boolean(formik.errors.title)}
                    helperText={formik.touched.title && formik.errors.title}
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                  />
                </Grid>
  {/* Poll Section */}
            {isNormalPost && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPollOption}
                      onChange={(e) => {
                        setShowPollOption(e.target.checked);
                        if (e.target.checked) {
                          formik.setFieldValue('poll.question', formik.values.title);
                        } else {
                          formik.setFieldValue('poll', { question: '', options: ['', ''] });
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PollIcon color="primary" />
                      Add a Poll
                    </Typography>
                  }
                  sx={{ mb: showPollOption ? 2 : 0 }}
                />

                {showPollOption && (
                  <Card variant="outlined" sx={{ borderStyle: 'dashed', bgcolor: 'background.paper' }}>
                    <CardContent sx={{ p: 2 }}>
                      {/* <TextField
                        fullWidth
                        label="Poll Question"
                        placeholder="Ask a question..."
                        name="poll.question"
                        value={formik.values.poll.question}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.poll?.question && Boolean(formik.errors.poll?.question)}
                        helperText={formik.touched.poll?.question && (formik.errors.poll as any)?.question}
                        sx={{ mb: 2 }}
                      /> */}

                      {formik.values.poll.options.map((_option: string, index: number) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`Option ${index + 1}`}
                            placeholder={`Enter option ${index + 1}...`}
                            name={`poll.options[${index}]`}
                            value={formik.values.poll.options[index]}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                              formik.submitCount > 0 && 
                              Array.isArray((formik.errors.poll as any)?.options) &&
                              Boolean((formik.errors.poll as any)?.options?.[index])
                            }
                          />
                          {formik.values.poll.options.length > 2 && (
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => handleRemovePollOption(index)}
                              sx={{ mt: 0.5 }}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Box>
                      ))}

                      <Button
                        startIcon={<Add />}
                        size="small"
                        onClick={handleAddPollOption}
                        disabled={formik.values.poll.options.length >= 5}
                        sx={{ mt: 1 }}
                      >
                        Add Option
                      </Button>
                      
                      {formik.submitCount > 0 && formik.errors.poll && typeof formik.errors.poll === 'object' && (formik.errors.poll as any)?.options && typeof (formik.errors.poll as any).options === 'string' && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                          {(formik.errors.poll as any).options}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Grid>
            )}
                {/* Content Field - Rich Text Editor */}
                <Grid size={{ xs: 12 }}>
                  <SlateRichTextEditor
                    value={formik.values.content}
                    onChange={handleContentChange}
                    onBlur={handleContentBlur}
                    error={formik.touched.content && Boolean(formik.errors.content)}
                    helperText={formik.touched.content && formik.errors.content ? String(formik.errors.content) : undefined}
                    placeholder="Share your thoughts, experiences, or updates with the community..."
                    maxLength={5000}
                  />
                </Grid>
                   {/* Location Field */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="location"
                label="Location (Optional)"
                placeholder="Where are you posting from?"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.location && Boolean(formik.errors.location)}
                helperText={formik.touched.location && formik.errors.location}
                slotProps={{
                  input: {
                    startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />,
                  },
                  htmlInput: { maxLength: 100 }
                }}
              />
            </Grid>
             {/* Tags Section */}
            <Grid size={{ xs: 12 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                HashTag
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    label="tags"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    disabled={formik.values.tags.length >= 10}
                    sx={{ flex: 1 }}
                    helperText={`${formik.values.tags.length}/10 tags`}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || formik.values.tags.length >= 10}
                    size="small"
                    startIcon={<Add />}
                    sx={{ height: 'fit-content', mt: 1 }}
                  >
                    Add
                  </Button>
                </Box>
                {formik.values.tags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formik.values.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={`#${tag}`}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>

          
              </>
            )}

         

            {/* Media Upload Section */}
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined" sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50', borderStyle: 'dashed' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUpload />
                    Media Upload
                  </Typography>
                  
                  {uploadingFiles && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Uploading files...</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                    </Box>
                  )}

                  {uploadedMedia.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Uploaded Media ({uploadedMedia.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {uploadedMedia.map((media, index) => (
                          <Chip
                            key={index}
                            label={media.originalName}
                            onDelete={() => handleRemoveMedia(index)}
                            color={media.type === 'image' ? 'primary' : 'secondary'}
                            size="small"
                            deleteIcon={
                              <Delete 
                                sx={{
                                  fontSize: '18px !important',
                                  color: 'error.main !important',
                                  '&:hover': { 
                                    color: 'error.dark !important' 
                                  }
                                }} 
                              />
                            }
                            sx={{
                              '& .MuiChip-deleteIcon': {
                                color: 'error.main !important',
                                '&:hover': {
                                  color: 'error.dark !important'
                                }
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {uploadedMedia.length  === 0 &&<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      component="label"
                      startIcon={<Photo />}
                      variant="outlined"
                      size="small"
                      disabled={uploadingFiles}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Images
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'image')}
                      />
                    </Button>
                    <Button
                      component="label"
                      startIcon={<AttachFile />}
                      variant="outlined"
                      size="small"
                      disabled={uploadingFiles}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Documents
                      <input
                        type="file"
                        hidden
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.xlsx,.ppt,.pptx"
                        onChange={(e) => handleFileUpload(e, 'document')}
                      />
                    </Button>
                  </Box>}
                </CardContent>
              </Card>
            </Grid>


          </Grid>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {isPreviewMode ? (
            // Preview Mode Buttons
            <>
              <Button onClick={handleBackToEdit} color="secondary" variant="outlined">
                Back to Edit
              </Button>
              <Button
                onClick={handleConfirmPublish}
                variant="contained"
                disabled={isCreating}
                startIcon={isCreating ? <CircularProgress size={16} /> : null}
                sx={{ minWidth: 120 }}
              >
                {isCreating ? 'Publishing...' : 'Publish'}
              </Button>
            </>
          ) : (
            // Edit Mode Buttons
            <>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={(e:any)=>formik.handleSubmit(e)}
                variant="contained"
                disabled={isCreating}
                startIcon={isCreating ? <CircularProgress size={16} /> : null}
                sx={{ minWidth: 120 }}
              >
                {isCreating ? 'Loading...' : 'Preview'}
              </Button>
            </>
          )}
        </DialogActions>
      {/* </form> */}
    </Dialog>
  );
};